import {
  PopoverContent,
  PopoverTrigger,
  MarkerColor,
  BadgeColor,
  Typography,
  Skeleton,
  Popover,
  Loading,
  Button,
  Marker,
  Badge,
  Card,
} from '@ui/components';
import { CompanyPolicyMiniResponse, CompanyPolicyResponse } from '@interfaces/models';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetEmployeeProfiles } from '@shared/hooks';
import { ArrowDown } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { Routes } from '@shared/enums';
import { useState, FC } from 'react';
import { cn } from '@ui/utils';

interface EmployeesProfilePickerProps {
  data?: CompanyPolicyResponse;
  isLoading?: boolean;
}

export const EmployeesProfilePicker: FC<EmployeesProfilePickerProps> = ({ data, isLoading }) => {
  const { employeeProfiles, isFetching } = useGetEmployeeProfiles();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { profileId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleProfileClick = (profile: CompanyPolicyMiniResponse) => {
    navigate(profileId ? `../${Routes.EMPLOYEE_PROFILES}/${profile.id}` : `${profile.id}`);
    setIsOpen(false);
    setSearch('');
  };

  const filteredProfiles = employeeProfiles.filter((option) =>
    option.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <Card
          className={cn(
            'p-4 h-full flex items-center justify-between cursor-pointer',
            employeeProfiles.length === 1 && 'pointer-events-none',
          )}
        >
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <>
              <div className="flex items-center gap-2 h-full">
                <Marker variant={data?.color as MarkerColor} className="h-1/2" />
                <Typography variant="h300">{data?.name}</Typography>
              </div>

              {data?.isDefault && (
                <Badge variant="blue" size="large">
                  {t('EMPLOYEE_PROFILES.DEFAULT_PROFILE')}
                </Badge>
              )}

              {!data?.isActive && (
                <Badge variant="neutral" size="large">
                  {t('EMPLOYEE_PROFILES.INACTIVE_PROFILE')}
                </Badge>
              )}
            </>
          )}
          {employeeProfiles.length > 1 && (
            <Button variant="ghost" size="small" icon>
              <ArrowDown />
            </Button>
          )}
        </Card>
      </PopoverTrigger>

      <PopoverContent
        className="min-w-[20vw] p-0 max-h-48 overflow-y-auto"
        sideOffset={-10}
        align="center"
        side="bottom"
      >
        <input
          className="p-2 border-b border-border-default w-full outline-none"
          placeholder={t('EMPLOYEE_PROFILES.PICKER_SEARCH_PLACEHOLDER')}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <Loading isLoading={isFetching} size="small">
          <div className="flex flex-wrap gap-2 p-2">
            {filteredProfiles.map((option) => (
              <Badge
                onClick={() => handleProfileClick(option)}
                variant={option.color as BadgeColor}
                key={option.id}
                size="large"
                clickable
              >
                {option.name}
              </Badge>
            ))}

            {!filteredProfiles.length && (
              <Typography variant="labelMediumBook" className="text-weak">
                {t('EMPLOYEE_PROFILES.PICKER_NO_RESULT')}
              </Typography>
            )}
          </div>
        </Loading>
      </PopoverContent>
    </Popover>
  );
};
